'use client';

import React, { useEffect, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { type ProductListItem } from '@/common/types/product.type';
import { type FilteredDataType } from '@/features/store/types/filter.type';

import { useGetUserRecommendedProducts } from '@/common/apis/recommendations/recommendations';
import {
  orderByLookup,
  orderMock,
} from '@/common/constants/filter-product-list.constant';
import { useSession } from '@/common/hooks/use-session.hook';
import RecommendationSection from '@/features/product/components/recommendation-section.component';

import FilterTags from './filter-tags.component';
import Products from './products.component';

interface StoreClientWrapperProps {
  allCategories: MultiLevelMenuItem[];
  vendorSlug: string;
  hasCollection: boolean;
  collectionsLength: number;
  renderBannerAndCollections?: React.ReactNode;
  initialProducts?: ProductListItem[];
  initialTotalCount?: number;
  vendorId: number;
}

function StoreRecommendations({ vendorId }: { vendorId: number }) {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const userRecommendedProducts = useGetUserRecommendedProducts({
    enabled: isAuthenticated,
    vendorId,
  });
  const hasVisibleSection =
    isAuthenticated &&
    (userRecommendedProducts.isLoading ||
      (!userRecommendedProducts.isError &&
        Boolean(
          userRecommendedProducts.data?.data.recommendedProducts?.length
        )));

  if (!hasVisibleSection) return null;

  return (
    <div className="-mx-4 mb-10 flex w-[calc(100%+2rem)] flex-col gap-10 overflow-hidden md:mx-0 md:w-full lg:mb-14 lg:gap-14">
      <RecommendationSection
        title="پیشنهاد برای شما"
        products={userRecommendedProducts.data?.data.recommendedProducts}
        isError={userRecommendedProducts.isError}
        isLoading={userRecommendedProducts.isLoading}
      />
    </div>
  );
}

export default function StoreClientWrapper({
  allCategories,
  vendorSlug,
  hasCollection,
  collectionsLength,
  renderBannerAndCollections,
  initialProducts = [],
  initialTotalCount = 0,
  vendorId,
}: StoreClientWrapperProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Parse query params from URL
  const categories = searchParams.get('category')?.split('-') || [];
  const orderBy = searchParams.get('orderBy') || '';
  const isDiscounted = searchParams.get('isDiscounted') === 'true';
  const isAvailable = searchParams.get('isAvailable') === 'true';

  const [searchByProductName, setSearchByProductName] = useState('');

  const [filteredData, setFilteredData] = useState<FilteredDataType>({
    category: [],
    orderBy: { value: '', label: '' },
    isDiscounted: false,
    isAvailable: false,
  });

  // Update filtered data when categories or query params change
  useEffect(() => {
    if (allCategories.length) {
      const defaultCategory: MultiLevelMenuItem[] = categories.map((item) => {
        return (
          allCategories.find(
            (category: { value: string }) => category.value === item
          ) || {
            label: '',
            value: '',
          }
        );
      });
      const defaultOrderBy = orderMock.find(
        (item) => item.value === orderBy
      ) || {
        label: '',
        value: '',
      };
      setFilteredData({
        category: defaultCategory,
        orderBy: defaultOrderBy,
        isDiscounted,
        isAvailable,
      });
    }
  }, [allCategories, categories.join('-'), orderBy, isDiscounted, isAvailable]);

  // Build query params for products
  const queryParams = {
    searchByProductName,

    categoryList: allCategories
      .filter((category: { value: string }) =>
        categories.includes(category.value)
      )
      .map((category: { label: string }) => category.label),

    sortCondition: orderByLookup[orderBy]
      ? orderByLookup[orderBy].sortCondition
      : undefined,

    sortDirection: orderByLookup[orderBy]
      ? orderByLookup[orderBy].sortDirection
      : undefined,

    isAvailable: isAvailable || false,

    isDiscounted: isDiscounted || false,

    pageSize: 10,
    pageIndex: 0,
  };

  return (
    <>
      {/* Banner and Collections section */}
      {renderBannerAndCollections}

      <StoreRecommendations vendorId={vendorId} />

      <FilterTags
        allCategories={allCategories}
        payload={queryParams}
        filteredData={filteredData}
        setFilteredData={setFilteredData}
        onSearch={(query) => {
          setSearchByProductName(query);
          const params = new URLSearchParams(searchParams);
          params.set('page', '1');
          const queryString = params.toString();
          router.replace(
            queryString ? `${pathname}?${queryString}` : pathname,
            { scroll: false }
          );
        }}
      />

      <div className="w-full overflow-hidden">
        <Products
          queryParams={{ ...queryParams, ...{ searchByProductName } }}
          hasCollection={hasCollection}
          collectionsLength={collectionsLength}
          isLoadingCollection={false}
          vendorSlug={vendorSlug}
          initialProducts={initialProducts}
          initialTotalCount={initialTotalCount}
        />
      </div>
    </>
  );
}
